import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { instagramAPI, crossPostAPI, tiktokAPI, videosAPI, getAssetUrl } from "../lib/api";
import { formatNumber, formatDate } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Loader2,
  Link as LinkIcon,
  Unlink,
  Users,
  Heart,
  Eye,
  MessageCircle,
  Bookmark,
  TrendingUp,
  Send,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Sparkles,
  RefreshCw,
  Trash2,
  Film,
  Plus,
  Hash,
  X,
  Download,
  Copy,
  ExternalLink,
  Check,
} from "lucide-react";

// Instagram Logo Component
const InstagramLogo = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

// TikTok Logo Component
const TikTokLogo = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function CrossPostPage() {
  const [loading, setLoading] = useState(true);
  const [igAccount, setIgAccount] = useState(null);
  const [tiktokAccount, setTiktokAccount] = useState(null);
  const [tiktokOAuthAccount, setTiktokOAuthAccount] = useState(null); // OAuth connected account
  const [igAnalytics, setIgAnalytics] = useState(null);
  const [crossPosts, setCrossPosts] = useState([]);
  const [availableVideos, setAvailableVideos] = useState([]);
  const [tiktokVideos, setTiktokVideos] = useState([]);
  
  // Dialog states
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [crossPostDialogOpen, setCrossPostDialogOpen] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  
  // Form states
  const [igUsername, setIgUsername] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [tiktokOAuthConnecting, setTiktokOAuthConnecting] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedSource, setSelectedSource] = useState("library");
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [hashtagInput, setHashtagInput] = useState("");
  const [adaptForReels, setAdaptForReels] = useState(true);
  const [scheduledTime, setScheduledTime] = useState("");
  const [posting, setPosting] = useState(false);
  const [adapting, setAdapting] = useState(false);
  
  // Export states
  const [exportData, setExportData] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generatingHashtags, setGeneratingHashtags] = useState(false);

  // Listen for TikTok OAuth callback messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === "tiktok-oauth-success") {
        toast.success(`Connected to TikTok as ${event.data.display_name}!`);
        setTiktokOAuthConnecting(false);
        fetchTiktokOAuthStatus();
      } else if (event.data.type === "tiktok-oauth-error") {
        toast.error(`TikTok connection failed: ${event.data.message}`);
        setTiktokOAuthConnecting(false);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const fetchTiktokOAuthStatus = async () => {
    try {
      const response = await tiktokAPI.getOAuthStatus();
      if (response.data.connected) {
        setTiktokOAuthAccount(response.data.account);
      }
    } catch (error) {
      console.error("Failed to fetch TikTok OAuth status:", error);
    }
  };

  const handleTiktokOAuthConnect = async () => {
    setTiktokOAuthConnecting(true);
    try {
      const response = await tiktokAPI.getOAuthLoginUrl();
      const authUrl = response.data.auth_url;
      
      // Open TikTok OAuth in popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      window.open(
        authUrl,
        "TikTok Login",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );
    } catch (error) {
      console.error("Failed to get TikTok auth URL:", error);
      toast.error("Failed to connect to TikTok. Please try again.");
      setTiktokOAuthConnecting(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchTiktokOAuthStatus();
  }, []);

  const fetchData = async () => {
    try {
      const [igRes, tiktokRes, crossPostsRes, videosRes] = await Promise.all([
        instagramAPI.getAccount(),
        tiktokAPI.getAccount(),
        crossPostAPI.list().catch(() => ({ data: [] })),
        videosAPI.getVideos().catch(() => ({ data: [] })),
      ]);
      
      setIgAccount(igRes.data);
      setTiktokAccount(tiktokRes.data);
      setCrossPosts(crossPostsRes.data || []);
      setAvailableVideos(videosRes.data || []);
      
      if (igRes.data) {
        const analyticsRes = await instagramAPI.getAnalytics();
        setIgAnalytics(analyticsRes.data);
      }
      
      if (tiktokRes.data) {
        const tiktokVidsRes = await tiktokAPI.getVideos(20, "views");
        setTiktokVideos(tiktokVidsRes.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectInstagram = async () => {
    if (!igUsername) {
      toast.error("Please enter your Instagram username");
      return;
    }

    setConnecting(true);
    try {
      const response = await instagramAPI.connect(igUsername);
      setIgAccount(response.data);
      setConnectDialogOpen(false);
      setIgUsername("");
      toast.success("Instagram account connected!");
      
      const analyticsRes = await instagramAPI.getAnalytics();
      setIgAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect Instagram");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await instagramAPI.disconnect();
      setIgAccount(null);
      setIgAnalytics(null);
      setCrossPosts([]);
      setDisconnectDialogOpen(false);
      toast.success("Instagram account disconnected");
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const handleAdaptCaption = async () => {
    if (!caption) return;
    
    setAdapting(true);
    try {
      const response = await crossPostAPI.adaptCaption(caption, hashtags);
      setCaption(response.data.adapted_caption);
      if (response.data.suggested_hashtags) {
        setHashtags(prev => [...new Set([...prev, ...response.data.suggested_hashtags])].slice(0, 15));
      }
      toast.success("Caption adapted for Instagram!");
    } catch (error) {
      toast.error("Failed to adapt caption");
    } finally {
      setAdapting(false);
    }
  };

  const handleCreateCrossPost = async () => {
    if (!selectedVideo || !caption) {
      toast.error("Please select a video and enter a caption");
      return;
    }

    setPosting(true);
    try {
      const response = await crossPostAPI.create({
        video_id: selectedVideo.id,
        source: selectedSource,
        caption,
        hashtags,
        scheduled_time: scheduledTime || null,
        adapt_for_reels: adaptForReels,
      });
      
      setCrossPosts(prev => [response.data, ...prev]);
      setCrossPostDialogOpen(false);
      resetForm();
      toast.success(scheduledTime ? "Cross-post scheduled!" : "Posted to Instagram Reels!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create cross-post");
    } finally {
      setPosting(false);
    }
  };

  const handlePostNow = async (postId) => {
    try {
      await crossPostAPI.postNow(postId);
      setCrossPosts(prev => prev.map(p => 
        p.id === postId ? { ...p, status: "posted", posted_time: new Date().toISOString() } : p
      ));
      toast.success("Posted to Instagram Reels!");
    } catch (error) {
      toast.error("Failed to post");
    }
  };

  const handleDeleteCrossPost = async (postId) => {
    try {
      await crossPostAPI.delete(postId);
      setCrossPosts(prev => prev.filter(p => p.id !== postId));
      toast.success("Cross-post deleted");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setSelectedVideo(null);
    setSelectedSource("library");
    setCaption("");
    setHashtags([]);
    setScheduledTime("");
  };

  const addHashtag = () => {
    if (hashtagInput && !hashtags.includes(hashtagInput)) {
      const tag = hashtagInput.startsWith("#") ? hashtagInput : `#${hashtagInput}`;
      setHashtags([...hashtags, tag]);
      setHashtagInput("");
    }
  };

  const removeHashtag = (tag) => {
    setHashtags(hashtags.filter(t => t !== tag));
  };

  const openCrossPostDialog = (video, source) => {
    setSelectedVideo(video);
    setSelectedSource(source);
    setCaption(video.title || video.description || "");
    setHashtags(video.hashtags || []);
    setCrossPostDialogOpen(true);
  };

  // Instagram Export Functions
  const openExportDialog = (video) => {
    setSelectedVideo(video);
    setCaption(video.title || video.script?.substring(0, 150) || "");
    setHashtags(video.hashtags || []);
    setExportData(null);
    setCopied(false);
    setExportDialogOpen(true);
  };

  const handlePrepareExport = async () => {
    if (!selectedVideo) return;
    
    setPreparing(true);
    try {
      const response = await instagramAPI.prepareExport({
        video_id: selectedVideo.id,
        caption: caption,
        hashtags: hashtags,
        include_cta: true,
      });
      
      setExportData(response.data);
      toast.success("Ready for Instagram!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to prepare export");
    } finally {
      setPreparing(false);
    }
  };

  const handleCopyCaption = async () => {
    if (!exportData?.full_caption) return;
    
    try {
      await navigator.clipboard.writeText(exportData.full_caption);
      setCopied(true);
      toast.success("Caption copied to clipboard!");
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error("Failed to copy. Please select and copy manually.");
    }
  };

  const handleDownloadForInstagram = () => {
    if (!exportData?.download_url) return;
    
    // Use the API URL for download
    const downloadUrl = getAssetUrl(exportData.download_url);
    window.open(downloadUrl, '_blank');
    toast.success("Download started! Check your downloads folder.");
  };

  const handleOpenInstagram = () => {
    // Try deep link first (mobile), fallback to web
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      window.location.href = "instagram://camera";
      // Fallback after a short delay if deep link doesn't work
      setTimeout(() => {
        window.open("https://www.instagram.com/", "_blank");
      }, 1500);
    } else {
      window.open("https://www.instagram.com/", "_blank");
    }
  };

  const handleGenerateHashtags = async () => {
    setGeneratingHashtags(true);
    try {
      const topic = selectedVideo?.title || caption.substring(0, 50);
      const response = await instagramAPI.generateHashtags(topic, 'general', 15);
      setHashtags(response.data.hashtags || []);
      toast.success("Hashtags generated!");
    } catch (error) {
      toast.error("Failed to generate hashtags");
    } finally {
      setGeneratingHashtags(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
      data-testid="cross-post-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <div className="flex items-center">
              <TikTokLogo className="w-7 h-7" />
              <ArrowRight className="w-5 h-5 mx-1 text-muted-foreground" />
              <InstagramLogo className="w-7 h-7 text-[#E4405F]" />
            </div>
            Cross-Post to Reels
          </h1>
          <p className="text-muted-foreground mt-2">
            Adapt and share your content across TikTok and Instagram Reels.
          </p>
        </div>
        
        {igAccount && (
          <Button
            onClick={() => setCrossPostDialogOpen(true)}
            className="bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45] hover:opacity-90 text-white font-bold rounded-xl gap-2"
            data-testid="new-crosspost-btn"
          >
            <Plus className="w-5 h-5" />
            New Cross-Post
          </Button>
        )}
      </div>

      {/* Instagram Connection Card */}
      {!igAccount ? (
        <motion.div variants={itemVariants}>
          <Card className="bg-card border-border/50">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#833AB4] via-[#E4405F] to-[#FCAF45] flex items-center justify-center">
                <InstagramLogo className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Connect Instagram</h2>
              <p className="text-muted-foreground max-w-md mx-auto mb-6">
                Link your Instagram account to cross-post your TikTok content as Reels.
              </p>
              <Button
                onClick={() => setConnectDialogOpen(true)}
                className="bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45] hover:opacity-90 text-white font-bold rounded-xl gap-2"
                data-testid="connect-instagram-btn"
              >
                <LinkIcon className="w-5 h-5" />
                Connect Instagram
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <>
          {/* Connected Accounts Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Instagram Account */}
            <motion.div variants={itemVariants}>
              <Card className="bg-gradient-to-br from-[#833AB4]/10 via-[#E4405F]/10 to-[#FCAF45]/10 border-border/50">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={igAccount.instagram_avatar}
                        alt={igAccount.instagram_display_name}
                        className="w-12 h-12 rounded-full"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white">{igAccount.instagram_display_name}</h3>
                          <Badge className="badge-success text-xs gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Connected
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">@{igAccount.instagram_username}</p>
                      </div>
                    </div>
                    <InstagramLogo className="w-6 h-6 text-[#E4405F]" />
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-lg font-bold text-white">{formatNumber(igAccount.follower_count)}</p>
                      <p className="text-xs text-muted-foreground">Followers</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">{formatNumber(igAccount.following_count)}</p>
                      <p className="text-xs text-muted-foreground">Following</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">{igAccount.posts_count}</p>
                      <p className="text-xs text-muted-foreground">Posts</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-white">{igAccount.reels_count}</p>
                      <p className="text-xs text-muted-foreground">Reels</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* TikTok Account */}
            <motion.div variants={itemVariants}>
              <Card className={`border-border/50 ${(tiktokAccount || tiktokOAuthAccount) ? "bg-gradient-to-br from-[#FF0050]/10 to-[#00F2EA]/10" : "bg-card"}`}>
                <CardContent className="p-6">
                  {/* Show OAuth account first if connected */}
                  {tiktokOAuthAccount ? (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          {tiktokOAuthAccount.avatar ? (
                            <img
                              src={tiktokOAuthAccount.avatar}
                              alt={tiktokOAuthAccount.display_name}
                              className="w-12 h-12 rounded-full"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF0050] to-[#00F2EA] flex items-center justify-center">
                              <TikTokLogo className="w-6 h-6 text-white" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-white">{tiktokOAuthAccount.display_name}</h3>
                              <Badge className="bg-green-500 text-white text-xs gap-1">
                                <CheckCircle className="w-3 h-3" />
                                OAuth
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">@{tiktokOAuthAccount.username}</p>
                          </div>
                        </div>
                        <TikTokLogo className="w-6 h-6" />
                      </div>
                      <div className="text-center py-2">
                        <Badge className="badge-success gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Ready to Post Videos to TikTok
                        </Badge>
                      </div>
                    </>
                  ) : tiktokAccount ? (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={tiktokAccount.tiktok_avatar}
                            alt={tiktokAccount.tiktok_display_name}
                            className="w-12 h-12 rounded-full"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-white">{tiktokAccount.tiktok_display_name}</h3>
                              <Badge className="badge-success text-xs gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Connected
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">@{tiktokAccount.tiktok_username}</p>
                          </div>
                        </div>
                        <TikTokLogo className="w-6 h-6" />
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-lg font-bold text-white">{formatNumber(tiktokAccount.follower_count)}</p>
                          <p className="text-xs text-muted-foreground">Followers</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-white">{formatNumber(tiktokAccount.following_count)}</p>
                          <p className="text-xs text-muted-foreground">Following</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-white">{formatNumber(tiktokAccount.likes_count)}</p>
                          <p className="text-xs text-muted-foreground">Likes</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-white">{tiktokAccount.video_count}</p>
                          <p className="text-xs text-muted-foreground">Videos</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <TikTokLogo className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                      <p className="text-muted-foreground text-sm">TikTok not connected</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={handleTiktokOAuthConnect}
                        disabled={tiktokOAuthConnecting}
                      >
                        {tiktokOAuthConnecting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          "Connect TikTok"
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Analytics & Cross-Posts */}
          <Tabs defaultValue="crossPosts" className="space-y-6">
            <TabsList className="bg-secondary/50">
              <TabsTrigger value="crossPosts">Cross-Posts</TabsTrigger>
              <TabsTrigger value="analytics">Instagram Analytics</TabsTrigger>
              <TabsTrigger value="videos">Select Videos</TabsTrigger>
            </TabsList>

            {/* Cross-Posts Tab */}
            <TabsContent value="crossPosts" className="space-y-6">
              {crossPosts.length === 0 ? (
                <Card className="bg-card border-border/50">
                  <CardContent className="py-12 text-center">
                    <Send className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">No Cross-Posts Yet</h3>
                    <p className="text-muted-foreground mb-4">Start sharing your content on Instagram Reels</p>
                    <Button
                      onClick={() => setCrossPostDialogOpen(true)}
                      className="bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45] hover:opacity-90 text-white"
                    >
                      Create First Cross-Post
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {crossPosts.map((post) => (
                    <motion.div key={post.id} variants={itemVariants}>
                      <Card className="bg-card border-border/50">
                        <CardContent className="p-4">
                          <div className="flex gap-4">
                            <img
                              src={post.thumbnail_url 
                                ? getAssetUrl(post.thumbnail_url)
                                : "https://via.placeholder.com/100"}
                              alt={post.original_title}
                              className="w-20 h-28 object-cover rounded-lg"
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-white">{post.original_title}</h4>
                                <Badge className={post.status === "posted" ? "badge-success" : "badge-trending"}>
                                  {post.status === "posted" ? "Posted" : "Scheduled"}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                {post.instagram_caption}
                              </p>
                              {post.status === "posted" && (
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Eye className="w-3 h-3" />
                                    {formatNumber(post.views)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Heart className="w-3 h-3" />
                                    {formatNumber(post.likes)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <MessageCircle className="w-3 h-3" />
                                    {formatNumber(post.comments)}
                                  </span>
                                </div>
                              )}
                              {post.status === "scheduled" && post.scheduled_time && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Scheduled for {formatDate(post.scheduled_time)}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col gap-2">
                              {post.status === "scheduled" && (
                                <Button
                                  size="sm"
                                  onClick={() => handlePostNow(post.id)}
                                  className="bg-[#E4405F] hover:bg-[#E4405F]/90 text-white text-xs"
                                >
                                  <Send className="w-3 h-3 mr-1" />
                                  Post Now
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleDeleteCrossPost(post.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="space-y-6">
              {igAnalytics && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: "Reels Views", value: igAnalytics.total_reels_views, icon: Eye, color: "#E4405F" },
                    { label: "Total Likes", value: igAnalytics.total_likes, icon: Heart, color: "#833AB4" },
                    { label: "Comments", value: igAnalytics.total_comments, icon: MessageCircle, color: "#FCAF45" },
                    { label: "Saves", value: igAnalytics.total_saves, icon: Bookmark, color: "#00F0FF" },
                  ].map((stat) => (
                    <Card key={stat.label} className="bg-card border-border/50">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                          <TrendingUp className="w-4 h-4 text-[#00FF94]" />
                        </div>
                        <p className="text-2xl font-bold text-white">{formatNumber(stat.value)}</p>
                        <p className="text-xs text-muted-foreground">{stat.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-card border-border/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#E4405F]/10 rounded-xl flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-[#E4405F]" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-white">{igAnalytics?.avg_engagement_rate || 0}%</p>
                        <p className="text-muted-foreground">Engagement Rate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border/50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#833AB4]/10 rounded-xl flex items-center justify-center">
                        <Users className="w-6 h-6 text-[#833AB4]" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-white">{formatNumber(igAnalytics?.reach || 0)}</p>
                        <p className="text-muted-foreground">Total Reach</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Library Videos */}
                <Card className="bg-card border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                      <Film className="w-5 h-5 text-[#00F0FF]" />
                      Your Videos
                    </CardTitle>
                    <CardDescription>
                      Export your videos to Instagram Reels
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      {availableVideos.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No videos in library. Create some videos first!</p>
                      ) : (
                        <div className="space-y-3">
                          {availableVideos.filter(v => v.status === 'pending' || v.status === 'approved').slice(0, 15).map((video) => (
                            <div
                              key={video.id}
                              className="flex items-center gap-3 p-3 rounded-lg bg-secondary/20 hover:bg-secondary/40 transition-colors"
                            >
                              <img
                                src={video.thumbnail_url ? getAssetUrl(video.thumbnail_url) : "https://via.placeholder.com/60"}
                                alt={video.title}
                                className="w-14 h-20 object-cover rounded-lg"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">{video.title}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {video.duration ? `${video.duration}s` : 'Auto'} • {video.generation_type || 'Standard'}
                                </p>
                                <div className="flex gap-1 mt-1 flex-wrap">
                                  {video.hashtags?.slice(0, 3).map((tag, i) => (
                                    <span key={i} className="text-xs text-[#00F0FF]">{tag}</span>
                                  ))}
                                </div>
                              </div>
                              <Button 
                                size="sm" 
                                onClick={() => openExportDialog(video)}
                                className="bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45] hover:opacity-90 text-white gap-1"
                              >
                                <InstagramLogo className="w-4 h-4" />
                                Export
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Quick Guide */}
                <Card className="bg-card border-border/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                      <InstagramLogo className="w-5 h-5 text-[#E4405F]" />
                      How to Post to Instagram Reels
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-r from-[#833AB4]/10 via-[#E4405F]/10 to-[#FCAF45]/10 border border-[#E4405F]/20">
                        <div className="w-8 h-8 rounded-full bg-[#E4405F]/20 flex items-center justify-center text-[#E4405F] font-bold text-sm">1</div>
                        <div>
                          <p className="text-sm font-medium text-white">Select a Video</p>
                          <p className="text-xs text-muted-foreground">Click "Export" on any video from your library</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-r from-[#833AB4]/10 via-[#E4405F]/10 to-[#FCAF45]/10 border border-[#E4405F]/20">
                        <div className="w-8 h-8 rounded-full bg-[#E4405F]/20 flex items-center justify-center text-[#E4405F] font-bold text-sm">2</div>
                        <div>
                          <p className="text-sm font-medium text-white">Optimize Caption</p>
                          <p className="text-xs text-muted-foreground">AI will create the perfect Instagram caption & hashtags</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-r from-[#833AB4]/10 via-[#E4405F]/10 to-[#FCAF45]/10 border border-[#E4405F]/20">
                        <div className="w-8 h-8 rounded-full bg-[#E4405F]/20 flex items-center justify-center text-[#E4405F] font-bold text-sm">3</div>
                        <div>
                          <p className="text-sm font-medium text-white">Download & Copy</p>
                          <p className="text-xs text-muted-foreground">Download the video and copy the caption to clipboard</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-r from-[#833AB4]/10 via-[#E4405F]/10 to-[#FCAF45]/10 border border-[#E4405F]/20">
                        <div className="w-8 h-8 rounded-full bg-[#E4405F]/20 flex items-center justify-center text-[#E4405F] font-bold text-sm">4</div>
                        <div>
                          <p className="text-sm font-medium text-white">Post to Instagram</p>
                          <p className="text-xs text-muted-foreground">Open Instagram, create a Reel, and paste your caption!</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t border-border/50">
                      <p className="text-xs text-muted-foreground text-center">
                        💡 Instagram doesn't allow automated Reel posting, but we make it as easy as possible!
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          {/* Disconnect Button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => setDisconnectDialogOpen(true)}
              className="border-destructive/50 text-destructive hover:bg-destructive/10"
            >
              <Unlink className="w-4 h-4 mr-2" />
              Disconnect Instagram
            </Button>
          </div>
        </>
      )}

      {/* Connect Instagram Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent className="bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
              <InstagramLogo className="w-6 h-6 text-[#E4405F]" />
              Connect Instagram
            </DialogTitle>
            <DialogDescription>
              Enter your Instagram username to connect your account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white">Instagram Username</Label>
              <Input
                placeholder="@yourusername"
                value={igUsername}
                onChange={(e) => setIgUsername(e.target.value)}
                className="h-12 bg-secondary/50 border-border/50"
                data-testid="instagram-username-input"
              />
            </div>
            <div className="p-3 bg-[#E4405F]/10 border border-[#E4405F]/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-[#E4405F] mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  <span className="text-white font-medium">Demo Mode:</span> This uses simulated data. 
                  For real Instagram integration, Meta Developer API credentials are required.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectDialogOpen(false)} className="border-border/50">
              Cancel
            </Button>
            <Button
              onClick={handleConnectInstagram}
              disabled={connecting || !igUsername}
              className="bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45] hover:opacity-90 text-white"
              data-testid="confirm-connect-instagram-btn"
            >
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cross-Post Dialog */}
      <Dialog open={crossPostDialogOpen} onOpenChange={setCrossPostDialogOpen}>
        <DialogContent className="bg-card border-border/50 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-[#E4405F]" />
              Cross-Post to Instagram Reels
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedVideo && (
              <div className="flex gap-4 p-3 bg-secondary/30 rounded-lg">
                <img
                  src={selectedVideo.thumbnail_url 
                    ? getAssetUrl(selectedVideo.thumbnail_url)
                    : "https://via.placeholder.com/80"}
                  alt={selectedVideo.title}
                  className="w-16 h-24 object-cover rounded"
                />
                <div>
                  <p className="font-medium text-white">{selectedVideo.title}</p>
                  <Badge variant="outline" className="mt-1 text-xs">
                    From {selectedSource === "library" ? "Library" : "TikTok"}
                  </Badge>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-white">Caption for Instagram</Label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleAdaptCaption}
                  disabled={adapting || !caption}
                  className="text-[#E4405F] gap-1"
                >
                  {adapting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  AI Adapt
                </Button>
              </div>
              <Textarea
                placeholder="Write your Instagram caption..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="min-h-[100px] bg-secondary/50 border-border/50"
                data-testid="crosspost-caption-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white">Hashtags</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add hashtag..."
                  value={hashtagInput}
                  onChange={(e) => setHashtagInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addHashtag()}
                  className="h-10 bg-secondary/50 border-border/50"
                />
                <Button onClick={addHashtag} variant="outline" size="icon" className="h-10 w-10 border-border/50">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {hashtags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="outline"
                    className="bg-[#E4405F]/10 text-[#E4405F] border-[#E4405F]/30 gap-1 cursor-pointer"
                    onClick={() => removeHashtag(tag)}
                  >
                    <Hash className="w-3 h-3" />
                    {tag.replace("#", "")}
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-[#00F0FF]" />
                <div>
                  <p className="text-sm text-white">Adapt for Reels</p>
                  <p className="text-xs text-muted-foreground">Optimize format & hashtags</p>
                </div>
              </div>
              <Switch checked={adaptForReels} onCheckedChange={setAdaptForReels} />
            </div>

            <div className="space-y-2">
              <Label className="text-white flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Schedule (Optional)
              </Label>
              <Input
                type="datetime-local"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="h-10 bg-secondary/50 border-border/50"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCrossPostDialogOpen(false); resetForm(); }} className="border-border/50">
              Cancel
            </Button>
            <Button
              onClick={handleCreateCrossPost}
              disabled={posting || !caption}
              className="bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45] hover:opacity-90 text-white gap-2"
              data-testid="confirm-crosspost-btn"
            >
              {posting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : scheduledTime ? (
                <>
                  <Clock className="w-4 h-4" />
                  Schedule
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Post Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect Dialog */}
      <Dialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <DialogContent className="bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white">Disconnect Instagram?</DialogTitle>
            <DialogDescription>
              This will remove your Instagram connection and all cross-post history.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisconnectDialogOpen(false)} className="border-border/50">
              Cancel
            </Button>
            <Button onClick={handleDisconnect} variant="destructive">
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Instagram Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-[600px] bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <InstagramLogo className="w-6 h-6 text-[#E4405F]" />
              Export to Instagram Reels
            </DialogTitle>
            <DialogDescription>
              {selectedVideo?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {!exportData ? (
              <>
                {/* Caption Input */}
                <div className="space-y-2">
                  <Label className="text-white">Caption</Label>
                  <Textarea
                    placeholder="Write your Instagram caption..."
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    className="min-h-[80px] bg-secondary/50 border-border/50"
                  />
                  <p className="text-xs text-muted-foreground">{caption.length}/150 characters (optimal for preview)</p>
                </div>

                {/* Hashtags */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-white">Hashtags</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleGenerateHashtags}
                      disabled={generatingHashtags}
                      className="text-[#00F0FF] hover:text-[#00F0FF]/80"
                    >
                      {generatingHashtags ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-1" />
                      )}
                      Generate with AI
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add hashtag..."
                      value={hashtagInput}
                      onChange={(e) => setHashtagInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addHashtag())}
                      className="bg-secondary/50 border-border/50"
                    />
                    <Button type="button" onClick={addHashtag} variant="secondary" size="icon">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {hashtags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="gap-1">
                        {tag}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => removeHashtag(tag)} />
                      </Badge>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={handlePrepareExport}
                  disabled={preparing || !caption}
                  className="w-full bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45] hover:opacity-90"
                >
                  {preparing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Optimizing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Optimize for Instagram
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                {/* Export Ready View */}
                <div className="space-y-4">
                  {/* Optimized Caption Preview */}
                  <div className="space-y-2">
                    <Label className="text-white">Optimized Caption (with hashtags)</Label>
                    <div className="p-3 bg-secondary/30 rounded-lg border border-border/50">
                      <pre className="text-sm text-white whitespace-pre-wrap font-sans">
                        {exportData.full_caption}
                      </pre>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button
                      onClick={handleCopyCaption}
                      variant="secondary"
                      className="gap-2"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 text-green-500" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy Caption
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={handleDownloadForInstagram}
                      className="bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Video
                    </Button>
                  </div>

                  {/* File Info */}
                  <p className="text-xs text-muted-foreground text-center">
                    File size: {exportData.file_size_mb} MB
                  </p>

                  {/* Open Instagram Button */}
                  <Button
                    onClick={handleOpenInstagram}
                    className="w-full bg-gradient-to-r from-[#833AB4] via-[#E4405F] to-[#FCAF45] hover:opacity-90 gap-2"
                  >
                    <InstagramLogo className="w-5 h-5" />
                    Open Instagram
                    <ExternalLink className="w-4 h-4" />
                  </Button>

                  {/* Instructions */}
                  <div className="p-3 bg-secondary/20 rounded-lg space-y-2">
                    <p className="text-sm font-medium text-white">Next steps:</p>
                    <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                      {exportData.instructions?.map((step, i) => (
                        <li key={i}>{step.replace(/^\d+\.\s*/, '')}</li>
                      ))}
                    </ol>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            {exportData && (
              <Button
                variant="outline"
                onClick={() => setExportData(null)}
              >
                Edit Caption
              </Button>
            )}
            <Button variant="ghost" onClick={() => setExportDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
